import React, { useEffect, useState } from 'react';
import moment from 'moment';
import CurrencyFormat from 'react-currency-format';
import cn from '@/utils/cn';
import { CloseIcon } from '@/components/Icons';
import Skeleton from '@/components/Skeleton';
import AvatarIcon from '@/components/AvatarIcon';
import Button from '@/components/Button';
import { batchPaymentsPage, scheduledPaymentModal } from '@/utils/translations';
import { DATE_FORMAT_FULL } from '@/utils/dateFormats';
import { buildStatementLine } from '@/utils/buildStatementLine';
import { PaymentRowKindEnum, TPaymentListItem } from '../types/ManagePayments';
import {
  STATUS_LABEL,
  STATUS_STYLE,
  isPaymentCreator,
  isScheduledWithoutMultiSign
} from '../data/paymentStatus';
import { PaymentActionVariantEnum } from '../data/paymentActions';
import { UnableToDeleteReasonEnum } from '../data/unableToDeleteReason';
import PaymentAvatar from './PaymentAvatar';
import DetailRow from './DetailRow';
import RejectPaymentModal from './RejectPaymentModal';
import AlreadyActionedModal from './AlreadyActionedModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import UnableToDeleteModal from './UnableToDeleteModal';
import PaymentOutcomeResult from './PaymentOutcomeResult';
import { DESTRUCTIVE_IDLE_CLASS } from './actionButtonClasses';
import useAuth from '@/hooks/useAuth';
import { useCustomerName } from '../hooks/useCustomerName';
import { useScheduledPaymentDetail } from '../hooks/useScheduledPaymentDetail';
import { useApprovalDisplayState } from '../hooks/useApprovalDisplayState';
import { useScheduledPaymentAction, TPaymentUpdate } from '../hooks/useScheduledPaymentAction';

// Both the details view and the post-rejection view label the dialog with their own heading.
const TITLE_ID = 'scheduled-payment-title';

interface ScheduledPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  payment: TPaymentListItem | null;
  /** Lets the table move/patch this row after a successful approve/reject, without a refetch. */
  onPaymentUpdate?: (update: TPaymentUpdate) => void;
  /** Lets the table remove this row after a successful delete, without a refetch. */
  onPaymentDelete?: (id: string) => void;
}

const ScheduledPaymentModal: React.FC<ScheduledPaymentModalProps> = ({
  isOpen,
  onClose,
  payment,
  onPaymentUpdate,
  onPaymentDelete
}) => {
  const createdByName = useCustomerName(payment?.createdByCustomerId);
  const isBatch = payment?.kind === PaymentRowKindEnum.BATCH;
  // scheduled_payment/pending_scheduled_payment rows already carry every statement field inline
  // (see paymentDetail on TPaymentListItem) - only a true batch group needs the per-payment fetch.
  const { detail: groupDetail, isLoading: isLoadingGroupDetail } = useScheduledPaymentDetail(
    payment?.id,
    isOpen && isBatch
  );
  const detail = isBatch ? groupDetail : payment?.paymentDetail ?? null;
  const isLoading = isBatch && isLoadingGroupDetail;

  const auth = useAuth();
  // Only ever reachable for a pending_scheduled_payment row (the only kind that carries
  // PENDING_APPROVAL) - fetched regardless of decision state so a reopened modal can tell "did I
  // already approve this" (and "has anyone at all") from the very first render, not just right
  // after clicking Approve. Shared with BatchModal - see useApprovalDisplayState - so the two
  // can't drift out of alignment the way they previously did.
  const {
    hasPersistedApproval,
    hasNoApprovalAuthority,
    isAwaitingAdditionalApproval,
    isCurrentUserApproved,
    isNoApprovalAuthority,
    isLoadingApprovers,
    refetch: refetchApprovers
  } = useApprovalDisplayState(payment, isOpen, auth?.customer?.id);

  const {
    variant: actionVariant,
    hasRejected,
    hasApprovedLocally,
    hasFinalApproval,
    hasImmediateFailure,
    hasDeleted,
    hasRetried,
    currentUserName,
    approve,
    isApproving,
    approveError,
    openDeleteDialog,
    isDeleteDialogOpen,
    closeDeleteDialog,
    confirmDelete,
    isDeleting,
    deleteError,
    unableToDeleteReason,
    dismissUnableToDelete,
    retry,
    isRetrying,
    retryError,
    isRejectDialogOpen,
    openRejectDialog,
    closeRejectDialog,
    confirmReject,
    isRejecting,
    rejectError,
    authorisationConflictOutcome,
    dismissAuthorisationConflict
  } = useScheduledPaymentAction(payment, {
    onPaymentUpdate,
    onPaymentDelete,
    hasPersistedApproval,
    hasNoApprovalAuthority,
    refetchApprovers
  });

  // No dedicated "payment deleted" screen exists - the row disappearing from the table already
  // communicates success, so close rather than leaving a stale detail view open on a payment that
  // (as far as the UI now shows) no longer exists. hasRetried is NOT included here - unlike
  // delete, a successful retry gets its own paymentComplete result screen below (see
  // useScheduledPaymentAction's retry()), so it must stay open until the user closes it.
  useEffect(() => {
    if (hasDeleted) onClose();
  }, [hasDeleted, onClose]);

  // Gates the one-time "you approved, other signatories are still required" result screen (see
  // hasApprovedLocally below) so it's shown right after approving, but not replayed every time the
  // same payment is reopened later in the same session - once the modal has been closed at all
  // since that approval, later opens land back on the normal detail view (with its persisted
  // "Approved by" banner) instead.
  const [hasSeenPendingApprovalOutcome, setHasSeenPendingApprovalOutcome] = useState(false);
  useEffect(() => {
    setHasSeenPendingApprovalOutcome(false);
  }, [payment?.id]);
  useEffect(() => {
    if (!isOpen && hasApprovedLocally) setHasSeenPendingApprovalOutcome(true);
  }, [isOpen, hasApprovedLocally]);

  if (!isOpen || !payment) return null;

  // The status pill otherwise just echoes the group's own state (STATUS_LABEL), but PENDING_
  // APPROVAL needs copy the state alone can't express - same source (useApprovalDisplayState) as
  // BatchModal, so the two can't drift out of alignment: isAwaitingAdditionalApproval is the wide
  // "has ANY signatory approved" check (every remaining approver sees this label, not just
  // whoever actually signed), colour still comes from STATUS_STYLE[payment.state] since it
  // doesn't need its own override.
  //
  // isNoApprovalAuthority checked first, kept as defense-in-depth even though
  // useApprovalDisplayState already excludes it from isAwaitingAdditionalApproval at the
  // source (the two are mutually exclusive by construction, not just by this ordering) -
  // see its module comment for why that exclusion lives there rather than here.
  const statusLabel = isNoApprovalAuthority
    ? batchPaymentsPage.noApprovalAuthorityStatus
    : isAwaitingAdditionalApproval
      ? scheduledPaymentModal.awaitingAdditionalApprovalStatusLabel
      : // An APPROVED payment with no multiSignActionId never actually went through anyone's
        // approval - it was simply scheduled. Colour still comes from STATUS_STYLE[payment.state]
        // (APPROVED's teal is correct here too) - only the label text changes.
        isScheduledWithoutMultiSign(payment.state, payment.multiSignActionId)
        ? batchPaymentsPage.status.scheduled
        : STATUS_LABEL[payment.state];

  // Delete is scoped to the payment's own creator - see isPaymentCreator - not every admin who
  // can otherwise reach this variant. Same rule BatchOverviewModal's batch-delete uses, and also
  // applied below to RETRY_AND_DELETE's own delete button, which previously had no creator check
  // at all. NO_APPROVAL_AUTHORITY must NOT grant delete: a payment still pending approval that the
  // current user isn't a signatory on is only deletable once it reaches APPROVED (or FAILED, via
  // RETRY_AND_DELETE below) - a non-authorising creator deleting it out of Action Required would
  // sidestep multi-sign approval entirely.
  const isCreator = isPaymentCreator(payment.createdByCustomerId, auth?.customer?.id);
  const showDeleteAction = actionVariant === PaymentActionVariantEnum.DELETE && isCreator;

  const statementValue = isLoading ? (
    <Skeleton
      count={2}
      containerClassName="items-end"
      ariaLabel={scheduledPaymentModal.loadingStatement}
    />
  ) : detail ? (
    <>
      <span className="block text-xs">
        {buildStatementLine(detail.payeeParticulars, detail.payeeAnalysis, detail.payeeReference)}
      </span>
      <span className="block text-xs">
        {buildStatementLine(detail.payerParticulars, detail.payerAnalysis, detail.payerReference)}
      </span>
    </>
  ) : (
    scheduledPaymentModal.emptyValue
  );

  return (
    <div
      className="fixed inset-0 z-99 flex justify-center overflow-y-auto bg-aux-lightgray"
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}>
      <button
        type="button"
        onClick={onClose}
        aria-label={scheduledPaymentModal.closeLabel}
        className="absolute right-6 top-6 flex h-[38px] w-[38px] items-center justify-center rounded-full border border-aux-gray bg-white hover:bg-emerge-base-3">
        <CloseIcon onClick={onClose} size="sm" />
      </button>

      {/* Anchored near the top, matching Pay/Batch Payment/Transfer Money's own modal layout
          (see e.g. src/pages/Home/TransferMoney/TransferMoney.tsx) - not vertically centered. */}
      <div className="w-107.5 max-w-full pt-16">
        {hasRejected ? (
          <PaymentOutcomeResult variant="paymentRejected" onClose={onClose} titleId={TITLE_ID} />
        ) : hasImmediateFailure ? (
          <PaymentOutcomeResult
            variant="paymentFailed"
            onClose={onClose}
            titleId={TITLE_ID}
            accountName={payment.payFromAccount?.name}
          />
        ) : hasRetried ? (
          <PaymentOutcomeResult
            variant="paymentComplete"
            onClose={onClose}
            titleId={TITLE_ID}
            accountName={payment.payFromAccount?.name}
          />
        ) : hasFinalApproval ? (
          <PaymentOutcomeResult variant="paymentScheduled" onClose={onClose} titleId={TITLE_ID} />
        ) : hasApprovedLocally && !hasSeenPendingApprovalOutcome ? (
          <PaymentOutcomeResult
            variant="paymentApproved"
            onClose={() => {
              setHasSeenPendingApprovalOutcome(true);
              onClose();
            }}
            titleId={TITLE_ID}
          />
        ) : (
          <>
            <div className="rounded-t-2xl border border-aux-gray bg-emerge-base-3 px-4 py-[18px]">
              <h4 id={TITLE_ID} className="text-center text-2xl font-medium text-emerge-navy">
                {scheduledPaymentModal.title}
              </h4>
            </div>

            <div className="flex flex-col gap-y-4 rounded-b-2xl border-x border-b border-aux-gray bg-white p-6">
              <CurrencyFormat
                value={Number(payment.totalAmount).toFixed(2)}
                displayType="text"
                thousandSeparator
                prefix="$"
                decimalScale={2}
                className="text-center text-5xl font-medium text-emerge-navy"
              />
              <span
                className={cn('text-center text-[13px] font-medium', STATUS_STYLE[payment.state])}>
                {statusLabel}
              </span>

              {/* Scoped to isCurrentUserApproved (this viewer specifically), not the wider
                  isAwaitingAdditionalApproval above - currentUserName is always this viewer's own
                  name, so crediting it here for someone else's approval would be wrong. Same
                  distinction BatchModal's "Approved by" row makes. */}
              {isCurrentUserApproved && (
                <div className="flex flex-col gap-y-3 rounded-lg bg-emerge-base-3 px-4 py-4">
                  <span className="text-sm font-medium text-emerge-navy">
                    {scheduledPaymentModal.approvedByLabel}
                  </span>
                  <div className="flex items-center gap-x-2">
                    <AvatarIcon
                      key={currentUserName}
                      name={currentUserName}
                      variant="xs"
                      className="border-2"
                    />
                    <span className="text-sm font-medium text-emerge-navy-2">
                      {currentUserName}
                    </span>
                  </div>
                </div>
              )}

              <span className="text-xs font-medium text-emerge-navy-2">
                {scheduledPaymentModal.payeeDetails}
              </span>
              <div className="flex min-h-[60px] items-center gap-x-2 rounded-lg bg-emerge-base-3 p-4">
                {/* Always the initials avatar - this modal only ever renders for
                    scheduled_payment/pending_scheduled_payment rows, which always use it (see
                    scheduledPaymentPresentationStrategy's renderAvatar). Built directly rather
                    than resolved via getPaymentPresentationStrategy to avoid a require cycle
                    (that registry's extensions file imports this very component - see
                    data/paymentPresentationStrategyExtensions.tsx). */}
                <PaymentAvatar size={50}>
                  <AvatarIcon
                    key={payment.name}
                    name={payment.name}
                    variant="xs"
                    className="h-full w-full"
                  />
                </PaymentAvatar>
                <div className="flex flex-col">
                  <span className="text-sm font-medium text-emerge-navy">{payment.name}</span>
                  <span className="text-xs font-medium text-emerge-navy-2">
                    {payment.payFromAccount?.accountNumber}
                  </span>
                </div>
              </div>

              <span className="text-xs font-medium text-emerge-navy-2">
                {scheduledPaymentModal.paymentDetails}
              </span>
              <div className="flex flex-col gap-y-0.5">
                <DetailRow label={scheduledPaymentModal.createdByLabel} value={createdByName} />
                <DetailRow
                  label={scheduledPaymentModal.fromLabel}
                  value={payment.payFromAccount?.name ?? scheduledPaymentModal.emptyValue}
                  filled
                />
                <DetailRow
                  label={scheduledPaymentModal.statementDetailsLabel}
                  value={statementValue}
                />
              </div>
              <div className="flex justify-center pt-2">
                <span className="rounded-full bg-emerge-base-3 px-4 py-1 text-2xs font-medium text-emerge-navy">
                  {moment(payment.scheduledDate).format(DATE_FORMAT_FULL)}
                </span>
              </div>

              {showDeleteAction && (
                <Button
                  variant="primary_inverted"
                  size="pill"
                  onClick={openDeleteDialog}
                  disabled={isDeleting}
                  className={cn(DESTRUCTIVE_IDLE_CLASS, 'mt-6 w-full')}>
                  {isDeleting
                    ? scheduledPaymentModal.deletingButton
                    : scheduledPaymentModal.deleteButton}
                </Button>
              )}

              {actionVariant === PaymentActionVariantEnum.RETRY_AND_DELETE && (
                <>
                  <div className="mt-6 flex items-center gap-x-[19px]">
                    {/* Same creator restriction as showDeleteAction above - retry (re-sending a
                        failed payment) stays available to any admin who can reach this variant,
                        only delete is creator-only. Pay now takes the full row when delete is
                        hidden, instead of leaving it stranded at its paired-with-delete width. */}
                    {isCreator && (
                      <Button
                        variant="primary_inverted"
                        size="pill"
                        onClick={openDeleteDialog}
                        disabled={isDeleting || isRetrying}
                        className={cn(DESTRUCTIVE_IDLE_CLASS, 'w-auto flex-1 whitespace-nowrap')}>
                        {isDeleting
                          ? scheduledPaymentModal.deletingButton
                          : scheduledPaymentModal.deleteButton}
                      </Button>
                    )}
                    <Button
                      size="pill"
                      onClick={retry}
                      disabled={isDeleting || isRetrying}
                      className={isCreator ? 'w-[185px]' : 'w-full'}>
                      {isRetrying
                        ? scheduledPaymentModal.retryingButton
                        : scheduledPaymentModal.payNowButton}
                    </Button>
                  </div>
                  {retryError && (
                    <p role="alert" className="text-center text-xs font-medium text-error-darkred">
                      {retryError}
                    </p>
                  )}
                </>
              )}

              {actionVariant === PaymentActionVariantEnum.APPROVAL_REQUIRED &&
                (isLoadingApprovers ? (
                  <p role="status" className="mt-6 text-center text-xs font-medium text-aux-gray">
                    {scheduledPaymentModal.loadingApprovers}
                  </p>
                ) : (
                  <>
                    <div className="mt-6 flex items-center gap-x-[19px]">
                      <Button
                        variant="primary_inverted"
                        size="pill"
                        onClick={openRejectDialog}
                        disabled={isApproving}
                        className={cn(DESTRUCTIVE_IDLE_CLASS, 'w-auto flex-1')}>
                        {scheduledPaymentModal.rejectButton}
                      </Button>
                      <Button
                        size="pill"
                        onClick={approve}
                        disabled={isApproving}
                        className="w-[185px]">
                        {isApproving
                          ? scheduledPaymentModal.approvingLabel
                          : scheduledPaymentModal.approveButton}
                      </Button>
                    </div>
                    {approveError && (
                      <p
                        role="alert"
                        className="text-center text-xs font-medium text-error-darkred">
                        {approveError}
                      </p>
                    )}
                  </>
                ))}

              {/* Personal banner - see isCurrentUserApproved above, must not show for a payment
                  someone else approved even though the status label still reads "Additional
                  approval required" for this viewer too. */}
              {isCurrentUserApproved && (
                <div className="mt-6 rounded-lg bg-emerge-teal-3 p-4">
                  <p className="text-sm font-medium text-emerge-navy">
                    {scheduledPaymentModal.approvedBannerTitle}
                  </p>
                  <p className="text-xs font-medium text-emerge-navy-2">
                    {scheduledPaymentModal.approvedBannerBody}
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <RejectPaymentModal
        isOpen={isRejectDialogOpen}
        onClose={closeRejectDialog}
        onConfirm={confirmReject}
        isSubmitting={isRejecting}
        error={rejectError}
      />

      <AlreadyActionedModal
        isOpen={!!authorisationConflictOutcome}
        outcome={authorisationConflictOutcome}
        isBatch={isBatch}
        onClose={() => {
          dismissAuthorisationConflict();
          onClose();
        }}
      />

      <DeleteConfirmModal
        isOpen={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isSubmitting={isDeleting}
        error={deleteError}
        headerText={scheduledPaymentModal.deleteConfirmTitle}
        bodyText={scheduledPaymentModal.deleteConfirmBody}
      />

      <UnableToDeleteModal
        isOpen={!!unableToDeleteReason}
        onClose={dismissUnableToDelete}
        headerText={scheduledPaymentModal.deleteBlockedTitle}
        bodyText={
          unableToDeleteReason === UnableToDeleteReasonEnum.WITHIN_BLOCK_WINDOW
            ? scheduledPaymentModal.deleteBlockedBody
            : scheduledPaymentModal.deleteConflictBody
        }
        buttonText={scheduledPaymentModal.closeButton}
      />
    </div>
  );
};

export default ScheduledPaymentModal;
